import { useEffect, useState } from "react";
import {
  ArrowLeft,
  FloppyDisk,
  Trash,
  Sparkle,
  ArrowClockwise,
  Play,
  CircleNotch,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  DURATION_OPTIONS,
  generateArticleBody,
  regeneratePart,
  errMessage,
} from "@/lib/api";

const wordCount = (s) => (s ? s.trim().split(/\s+/).filter(Boolean).length : 0);
const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export const GenerationWizard = ({
  article,
  onBack,
  onDelete,
  onArticleUpdated,
  onStartProcessing,
  onSaveMeta,
}) => {
  const [duration, setDuration] = useState(article.duration || 10);
  const [generating, setGenerating] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState(article.title);
  const [parts, setParts] = useState(article.parts || []);
  const [content, setContent] = useState(article.content || "");
  const isDraft = !article.id;

  useEffect(() => {
    setTitle(article.title);
    setParts(article.parts || []);
    setContent(article.content || "");
    setDuration(article.duration || 10);
  }, [article.id, article.updated_at]);

  const hasParts = parts && parts.length > 0;

  const handleGenerate = async () => {
    if (!article.id) {
      toast.error("Önce makale kaydedilmeli. 'Kaydet' butonuna basın.");
      return;
    }
    setGenerating(true);
    try {
      const updated = await generateArticleBody(article.id, duration);
      setParts(updated.parts);
      onArticleUpdated(updated);
      toast.success(`Makale üretildi: ${updated.parts.length} part`);
    } catch (e) {
      toast.error(errMessage(e, "Üretim başarısız"));
    } finally {
      setGenerating(false);
    }
  };

  const handleRegeneratePart = async (partId) => {
    setRegeneratingId(partId);
    try {
      const updated = await regeneratePart(article.id, partId);
      setParts(updated.parts);
      onArticleUpdated(updated);
      toast.success("Part yeniden üretildi");
    } catch (e) {
      toast.error(errMessage(e, "Yeniden üretim başarısız"));
    } finally {
      setRegeneratingId(null);
    }
  };

  const handlePartEdit = (partId, patch) => {
    setParts((prev) => prev.map((p) => (p.id === partId ? { ...p, ...patch } : p)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveMeta({
        title,
        content,
        parts: hasParts ? parts : undefined,
        duration: duration || undefined,
      });
      toast.success("Kaydedildi");
      onBack();
    } catch {
      toast.error("Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleStartProcessing = async () => {
    try {
      setSaving(true);
      await onSaveMeta({
        title,
        parts: hasParts ? parts : undefined,
        duration: duration || undefined,
      });
    } catch {
      toast.error("Kayıt başarısız");
      setSaving(false);
      return;
    }
    setSaving(false);
    onStartProcessing(article.id);
  };

  return (
    <div className="h-full flex flex-col bg-background" data-testid="generation-wizard">
      {/* Top action bar */}
      <div className="p-4 border-b border-border/60 flex items-center justify-between">
        <button
          data-testid="wizard-back-btn"
          onClick={onBack}
          className="h-9 px-3 flex items-center gap-2 border border-border hover:border-white text-muted-foreground hover:text-white transition-colors text-xs uppercase tracking-wider"
        >
          <ArrowLeft size={14} />
          Geri
        </button>
        <div className="flex items-center gap-2">
          {!isDraft && (
            <button
              data-testid="wizard-delete-btn"
              onClick={() => {
                if (window.confirm("Bu makale silinsin mi?")) onDelete(article.id);
              }}
              className="h-9 px-3 flex items-center gap-2 border border-border hover:border-primary hover:text-primary text-muted-foreground transition-colors text-xs uppercase tracking-wider"
            >
              <Trash size={13} />
              Sil
            </button>
          )}
          {hasParts && (
            <Button
              data-testid="wizard-process-btn"
              onClick={handleStartProcessing}
              disabled={saving || !article.id}
              className="rounded-none h-9 bg-white text-black hover:bg-[#EAEAEA] font-bold uppercase tracking-wider text-xs"
            >
              <Play size={14} weight="fill" className="mr-2" />
              İşleme Başla
            </Button>
          )}
          <Button
            data-testid="wizard-save-btn"
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="rounded-none h-9 bg-primary hover:bg-[#D63028] text-white font-bold uppercase tracking-wider text-xs"
          >
            <FloppyDisk size={14} weight="bold" className="mr-2" />
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-3xl mx-auto">
          <span className="label-mono text-primary">
            {isDraft
              ? "// new_draft"
              : hasParts
                ? `// article · ${parts.length} parts · ${article.duration || duration}min`
                : "// article · draft"}
          </span>
          <Input
            data-testid="wizard-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Makale başlığı…"
            className="mt-3 heading-font font-black text-3xl tracking-tight bg-transparent border-0 border-b border-border rounded-none h-auto py-3 px-0 focus-visible:ring-0 focus-visible:border-primary text-white placeholder:text-[#3A3A3A]"
          />
          {article.updated_at && !isDraft && (
            <p className="label-mono text-muted-foreground mt-2">
              Son güncelleme: {formatDate(article.updated_at)}
            </p>
          )}

          {!hasParts && (
            <SetupBlock
              duration={duration}
              onDurationChange={setDuration}
              generating={generating}
              onGenerate={handleGenerate}
              content={content}
              onContentChange={setContent}
              disabled={!article.id}
            />
          )}

          {hasParts && (
            <PartsEditor
              parts={parts}
              onPartEdit={handlePartEdit}
              onRegenerate={handleRegeneratePart}
              regeneratingId={regeneratingId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const SetupBlock = ({
  duration,
  onDurationChange,
  generating,
  onGenerate,
  content,
  onContentChange,
  disabled,
}) => (
  <div className="mt-8 space-y-8">
    <section
      data-testid="setup-generation-block"
      className="border border-border p-6 bg-[#0D0D0D]"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <span className="label-mono text-primary">// ai_generate</span>
          <h3 className="heading-font font-black text-xl tracking-tight mt-1">
            Makale Üret
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Video süresini seç, Gemini 3 Flash tüm makaleyi bölümlere ayırarak üretsin.
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-end gap-4">
        <div className="w-40">
          <label className="label-mono text-muted-foreground block mb-2">Süre (dk)</label>
          <Select
            value={String(duration)}
            onValueChange={(v) => onDurationChange(Number(v))}
          >
            <SelectTrigger
              data-testid="wizard-duration-trigger"
              className="bg-background border-border rounded-none h-11 focus:ring-primary focus:ring-1 focus:ring-offset-0"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-border rounded-none">
              {DURATION_OPTIONS.map((d) => (
                <SelectItem
                  key={d}
                  value={String(d)}
                  data-testid={`duration-option-${d}`}
                  className="rounded-none font-mono focus:bg-[#1A1A1A]"
                >
                  {d} dakika
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          data-testid="wizard-generate-btn"
          onClick={onGenerate}
          disabled={generating || disabled}
          className="rounded-none h-11 bg-primary hover:bg-[#D63028] text-white font-bold uppercase tracking-wider text-xs px-6"
        >
          {generating ? (
            <>
              <CircleNotch size={16} className="mr-2 animate-spin" />
              Üretiliyor… (~30-60 sn)
            </>
          ) : (
            <>
              <Sparkle size={16} weight="fill" className="mr-2" />
              Makale Üret
            </>
          )}
        </Button>
      </div>
      {disabled && (
        <p className="label-mono text-muted-foreground mt-3">
          Önce başlığı kaydet (üstteki &quot;Kaydet&quot; butonu), sonra AI üretimini başlat.
        </p>
      )}
    </section>

    <div className="flex items-center gap-4">
      <div className="h-px flex-1 bg-border" />
      <span className="label-mono text-muted-foreground">veya manuel yaz</span>
      <div className="h-px flex-1 bg-border" />
    </div>

    <section>
      <label className="label-mono text-muted-foreground block mb-2">Serbest Not</label>
      <Textarea
        data-testid="wizard-content-textarea"
        value={content}
        onChange={(e) => onContentChange(e.target.value)}
        placeholder="AI üretimi kullanmadan doğrudan yazmak istersen…"
        className="min-h-[280px] bg-transparent border border-border rounded-none focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0 text-[15px] leading-relaxed p-4 font-body"
      />
      <p className="label-mono text-muted-foreground mt-2">
        {wordCount(content)} words
      </p>
    </section>
  </div>
);

const PartsEditor = ({ parts, onPartEdit, onRegenerate, regeneratingId }) => {
  const totalWords = parts.reduce((sum, p) => sum + wordCount(p.body), 0);
  return (
    <div className="mt-8" data-testid="parts-editor">
      <div className="flex items-center justify-between mb-4">
        <span className="label-mono text-muted-foreground">
          {parts.length} parts · {totalWords} words
        </span>
      </div>
      <ul className="space-y-6">
        {parts.map((p, idx) => {
          const busy = regeneratingId === p.id;
          return (
            <li
              key={p.id}
              data-testid={`part-item-${p.id}`}
              className="border border-border bg-[#0D0D0D] animate-fade-in-up"
            >
              <div className="p-4 border-b border-border flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <span className="label-mono text-primary shrink-0">
                    #{String(idx + 1).padStart(2, "0")}
                  </span>
                  <Input
                    data-testid={`part-heading-${p.id}`}
                    value={p.heading}
                    onChange={(e) => onPartEdit(p.id, { heading: e.target.value })}
                    className="heading-font font-bold text-base tracking-tight bg-transparent border-0 rounded-none h-8 py-0 px-0 focus-visible:ring-0 text-white"
                  />
                </div>
                <button
                  data-testid={`part-regen-${p.id}`}
                  onClick={() => onRegenerate(p.id)}
                  disabled={busy}
                  className="h-8 px-3 flex items-center gap-2 border border-border hover:border-primary hover:text-primary text-muted-foreground disabled:opacity-50 transition-colors label-mono shrink-0"
                >
                  {busy ? (
                    <>
                      <CircleNotch size={12} className="animate-spin" />
                      Üretiliyor
                    </>
                  ) : (
                    <>
                      <ArrowClockwise size={12} />
                      Yeniden Üret
                    </>
                  )}
                </button>
              </div>
              <div className="p-4">
                <Textarea
                  data-testid={`part-body-${p.id}`}
                  value={p.body}
                  onChange={(e) => onPartEdit(p.id, { body: e.target.value })}
                  className="min-h-[160px] bg-transparent border border-border rounded-none focus-visible:ring-primary focus-visible:ring-1 focus-visible:ring-offset-0 text-[14.5px] leading-relaxed p-3 font-body"
                />
                <p className="label-mono text-muted-foreground mt-2">
                  {wordCount(p.body)} words
                </p>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-8 border border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
        <ImageIcon size={20} color="#FF3B30" weight="fill" className="mt-0.5 shrink-0" />
        <div>
          <p className="heading-font font-bold text-sm text-white">
            Sıradaki adım: İşleme Başla
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            &quot;İşleme Başla&quot; butonuna basınca AI, makaleyi 2-3 cümlelik sahnelere böler ve her
            sahne için Gemini görsel üretim prompt&apos;u hazırlar. Ardından tek tıkla görselleri
            oluşturabilirsin.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GenerationWizard;
